import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../components/icon';
import SelectField from '../components/select-field';
import {
  listManagers,
} from '../api/panel-api';

export function personLabel(item) {
  if (!item) return '';
  return `${item.last_name || ''} ${item.first_name || ''}`.trim();
}

export function isRd(item) {
  return item && (item.cs_role === 'rd' || item.cs_role === 'division_head');
}

export function isConsultant(item) {
  return item && (item.cs_role === 'consultant' || item.is_consultant);
}

export function divisionHeadLabel(division) {
  if (!division) return '';
  if (division.head) return personLabel(division.head);
  return division.head_name || '';
}

export function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return Math.round(n).toLocaleString('ru-RU').replace(/\u00A0/g, ' ');
}

export async function loadWorkingStaff() {
  const items = [];
  let page = 1;
  let total = 0;
  do {
    const data = await listManagers({
      page,
      perPage: 100,
      filters: { status: 'working' },
    });
    items.push(...(data.items || []));
    total = data.total || items.length;
    page += 1;
  } while (items.length < total);
  return items;
}

function measureMenu(button) {
  const rect = button.getBoundingClientRect();
  const maxHeight = 320;
  const spaceBelow = window.innerHeight - rect.bottom - 8;
  const spaceAbove = rect.top - 8;
  const openUp = spaceBelow < 160 && spaceAbove > spaceBelow;
  const room = Math.max(80, Math.min(maxHeight, openUp ? spaceAbove : spaceBelow));
  return {
    left: rect.left,
    width: Math.max(rect.width, 240),
    maxHeight: room,
    top: openUp ? 'auto' : rect.bottom + 4,
    bottom: openUp ? window.innerHeight - rect.top + 4 : 'auto',
  };
}

export function DivisionSelect({ value, label, divisions, disabled, onPick, onCreate }) {
  const listId = useId();
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const current = value || '';

  useEffect(() => {
    if (!open) return undefined;
    function onDoc(event) {
      const target = event.target;
      if (buttonRef.current && buttonRef.current.contains(target)) return;
      if (menuRef.current && menuRef.current.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  function toggle() {
    if (disabled) return;
    if (open) {
      setOpen(false);
      return;
    }
    if (buttonRef.current) setMenuStyle(measureMenu(buttonRef.current));
    setOpen(true);
  }

  const options = [
    { value: '', label: 'Вне дивизиона', note: '' },
    ...divisions.map((item) => ({
      value: item.id,
      label: item.title,
      note: divisionHeadLabel(item),
    })),
  ];

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={open ? 'meta meta--select is-open' : 'meta meta--select'}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={toggle}
      >
        <span>Дивизион</span>
        <strong>{label}</strong>
        <Icon name={open ? 'chevron-up' : 'chevron-down'} size={16} />
      </button>
      {open ? createPortal(
        <div
          ref={menuRef}
          className="select-field__menu"
          id={listId}
          role="listbox"
          style={menuStyle || undefined}
        >
          {options.map((option) => {
            const selected = option.value === current;
            return (
              <button
                key={option.value || 'none'}
                type="button"
                role="option"
                className={selected ? 'select-field__option is-selected' : 'select-field__option'}
                aria-selected={selected}
                onClick={() => {
                  setOpen(false);
                  onPick(option.value || null);
                }}
              >
                <span className="select-field__option-copy">
                  <span className="select-field__option-label">{option.label}</span>
                  {option.note ? <span className="select-field__option-note">{option.note}</span> : null}
                </span>
                {selected ? (
                  <span className="select-field__check">
                    <Icon name="check" size={16} tone="inherit" />
                  </span>
                ) : null}
              </button>
            );
          })}
          <button
            type="button"
            className="select-field__option division-menu__create"
            onClick={() => {
              setOpen(false);
              onCreate();
            }}
          >
            <span className="select-field__option-copy">
              <span className="select-field__option-label">+ Создать дивизион</span>
            </span>
          </button>
        </div>,
        document.body,
      ) : null}
    </>
  );
}

export function moveRdCopy(manager, fromTitle, target, outside) {
  const selfLast = manager.last_name || personLabel(manager);
  const fromName = fromTitle || 'дивизион';
  if (outside) {
    return `«${fromName}» останется без руководителя, ${selfLast} станет менеджером. Клиенты сохранятся.`;
  }
  const toName = target && target.title ? target.title : 'дивизион';
  const other = divisionHeadLabel(target);
  const otherLast = other.split(' ')[0] || other;
  const otherId = target.head_manager_id;
  if (other && otherId && otherId !== manager.id) {
    return `${otherLast} станет менеджером «${toName}», «${fromName}» останется без руководителя. Клиенты сохранятся у обеих.`;
  }
  return `«${toName}» получит руководителя, «${fromName}» останется без руководителя. Клиенты сохранятся.`;
}

export function MoveRdDialog({ manager, fromTitle, target, outside, pending, error, onClose, onConfirm }) {
  const [comment, setComment] = useState('');
  const name = personLabel(manager);
  const toLabel = outside ? 'Вне дивизиона' : (target && target.title) || '';
  return (
    <div className="overlay overlay--front">
      <div className="dialog" role="dialog" aria-modal="true">
        <div className="dialog__head">
          <h2>Перевести руководителя</h2>
          <button type="button" className="icon-button" aria-label="Закрыть" onClick={onClose}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div className="dialog__body">
          <div className="who">
            <strong>{name}</strong>
            <span>{fromTitle || 'Вне дивизиона'} → {toLabel}</span>
          </div>
          <div className="note note--warning">
            <Icon name="alert" size={18} />
            <p>{moveRdCopy(manager, fromTitle, target, outside)}</p>
          </div>
          <textarea
            className="comment"
            placeholder="Комментарий"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
          {error ? <p className="empty">{error}</p> : null}
        </div>
        <div className="dialog__footer">
          <button type="button" className="btn btn--secondary btn--small" onClick={onClose}>Отмена</button>
          <button type="button" className="btn btn--small" disabled={pending} onClick={onConfirm}>Перевести</button>
        </div>
      </div>
    </div>
  );
}

function dismissTitle(manager) {
  if (isConsultant(manager)) return 'Уволить консультанта';
  if (isRd(manager)) return 'Уволить руководителя дивизиона';
  return 'Уволить менеджера';
}

function clientsLine(count, inactive) {
  const n = Number(count) || 0;
  let text = `${formatCount(n)} закреплённых клиентов`;
  const idle = Number(inactive) || 0;
  if (idle) text += ` (в том числе ${formatCount(idle)} неактивных)`;
  return text;
}

export function DismissDialog({
  manager,
  divisionTitle,
  recipients,
  pending,
  error,
  onClose,
  onConfirm,
}) {
  const hasClients = !isConsultant(manager) && (Number(manager.clients_count) || 0) > 0;
  const canTransfer = recipients.length > 0;
  const [clients, setClients] = useState(hasClients && canTransfer ? 'transfer' : 'release');
  const [account, setAccount] = useState('delete');
  const [recipientId, setRecipientId] = useState(recipients[0] ? recipients[0].id : '');
  const [comment, setComment] = useState('');
  const name = personLabel(manager);
  const recipientOptions = recipients.map((item) => ({
    value: item.id,
    label: personLabel(item),
  }));
  const transferReady = !hasClients || clients === 'release' || Boolean(recipientId);

  function submit() {
    const body = { account };
    if (hasClients) {
      body.clients = clients;
      if (clients === 'transfer') body.cs_manager_id = recipientId;
    } else {
      body.clients = null;
    }
    const recipient = recipients.find((item) => item.id === recipientId);
    onConfirm(body, {
      account,
      clients: hasClients ? clients : null,
      recipient,
    });
  }

  return (
    <div className="overlay overlay--front">
      <div className="dialog" role="dialog" aria-modal="true">
        <div className="dialog__head">
          <h2><Icon name="trash" size={22} />{dismissTitle(manager)}</h2>
          <button type="button" className="icon-button" aria-label="Закрыть" onClick={onClose}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div className="dialog__body">
          <div className="who">
            <strong>{name}</strong>
            <span>{manager.email}</span>
          </div>
          {hasClients ? (
            <fieldset className="choice-block">
              <legend>{clientsLine(manager.clients_count, manager.inactive_clients_count)}</legend>
              <div className="choice-stack">
                <button
                  type="button"
                  className="choice"
                  disabled={!canTransfer}
                  onClick={() => canTransfer && setClients('transfer')}
                >
                  <span className={clients === 'transfer' ? 'radio is-on' : 'radio'} />
                  <span>Передать менеджеру</span>
                </button>
                {clients === 'transfer' && canTransfer ? (
                  <SelectField
                    label="Менеджер"
                    required={false}
                    value={recipientId}
                    onChange={setRecipientId}
                    options={recipientOptions}
                  />
                ) : null}
              </div>
              <button type="button" className="choice" onClick={() => setClients('release')}>
                <span className={clients === 'release' ? 'radio is-on' : 'radio'} />
                <span>Оставить клиентов без привязки</span>
              </button>
              {!canTransfer ? (
                <div className="note note--warning">
                  <Icon name="alert" size={18} />
                  <p>Других менеджеров нет. Клиенты останутся без привязки.</p>
                </div>
              ) : null}
            </fieldset>
          ) : null}
          <fieldset className="choice-block">
            <legend>Учётная запись в панели</legend>
            <button type="button" className="choice" onClick={() => setAccount('delete')}>
              <span className={account === 'delete' ? 'radio is-on' : 'radio'} />
              <span>Удалить</span>
            </button>
            <button type="button" className="choice" onClick={() => setAccount('keep')}>
              <span className={account === 'keep' ? 'radio is-on' : 'radio'} />
              <span>Оставить</span>
            </button>
          </fieldset>
          {isRd(manager) && divisionTitle ? (
            <div className="note note--warning">
              <Icon name="alert" size={18} />
              <p>Дивизион «{divisionTitle}» останется без руководителя.</p>
            </div>
          ) : null}
          <textarea
            className="comment"
            placeholder="Комментарий"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
          {error ? <p className="empty">{error}</p> : null}
        </div>
        <div className="dialog__footer">
          <button type="button" className="btn btn--secondary btn--small" onClick={onClose}>Отмена</button>
          <button
            type="button"
            className="btn btn--danger btn--small"
            disabled={pending || !transferReady}
            onClick={submit}
          >
            Уволить
          </button>
        </div>
      </div>
    </div>
  );
}

export function dismissToast(manager, outcome) {
  const name = personLabel(manager);
  const deleted = outcome.account === 'delete';
  const transferred = outcome.clients === 'transfer' && outcome.recipient;
  let title = `${name} уволен`;
  if (deleted && transferred) title = `${name} уволен и удалён из панели, база передана`;
  else if (deleted) title = `${name} уволен и удалён из панели`;
  else if (transferred) title = `${name} уволен, база передана`;
  let emptyLead = '';
  if (transferred) emptyLead = `База передана ${personLabel(outcome.recipient)} при увольнении.`;
  else if (outcome.clients === 'release') emptyLead = 'Клиенты оставлены без привязки.';
  return { title, emptyLead };
}

export function moveToast(manager, title) {
  const name = personLabel(manager);
  if (!title) return `${name} теперь вне дивизиона`;
  return `${name} теперь в дивизионе «${title}»`;
}
